import { datadogLogs } from '@datadog/browser-logs';
import { Cloud } from '@silbo/core';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useBandwidthContext } from '../../../contexts/BandwidthContext';
import { useQueryQueueContext } from '../../../contexts/QueryQueueContext';
import toast from '../../../toast';
import { PartialIncident, ReportIncidentDialog } from '../components/ReportIncidentDialog/ReportIncidentDialog';

interface ReportIncidentPageProps {
  fareId: string;
}

const ReportIncidentPage = ({ fareId }: ReportIncidentPageProps) => {
  const navigate = useNavigate();

  const { addQuery, hasQueriesPending } = useQueryQueueContext();
  const { status } = useBandwidthContext();

  const { t } = useTranslation('patient-transport');

  const { mutate, isPending } = useMutation({
    mutationFn: async ({ fareId, incident }: { fareId: string; incident: PartialIncident }) => {
      await Cloud.createIncident({ fareId, ...incident });
    },
    onError: (error, { fareId, incident }) => {
      toast.error(t('reportIncident.creationError'));
      datadogLogs.logger.error(
        'create-incident-error',
        {
          fareId,
          incident,
        },
        error
      );
    },
    onSuccess: () => {
      toast.success(t('reportIncident.creationSuccess'));
      navigate({ to: '/patient-transport/fares' });
    },
  });

  const handleIncident = useCallback(
    async ({ fareId, incident }: { fareId: string; incident: PartialIncident }) => {
      mutate({ fareId, incident });
    },
    [mutate]
  );

  const isOfflineMode = hasQueriesPending(fareId) && status !== 'online';

  return (
    <ReportIncidentDialog
      open
      onClose={() => navigate({ to: '/patient-transport/fares' })}
      onComplete={(incident) =>
        addQuery({
          id: JSON.stringify({ fareId, incident }),
          queryFn: () => handleIncident({ fareId, incident }),
        })
      }
      isLoading={isPending}
      isOfflineMode={isOfflineMode}
    />
  );
};

export default ReportIncidentPage;
